import React, { useMemo, useState } from "react";
import { Phone } from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/sections/Breadcrumbs";
import { BUSINESS, UI, SERVICES, trackEvent, trackAction, trackA11yEvent, fireAdsConversion } from "@/content/site";
import { submitContact } from "@/lib/api";

// Module-level constant so schema.about.address doesn't change identity across renders.
const CONTACT_ADDRESS = {
  "@type": "PostalAddress",
  "streetAddress": "1015 N Bishop Ave",
  "addressLocality": "Dallas",
  "addressRegion": "TX",
  "postalCode": "75208",
  "addressCountry": "US",
};

export default function ContactPage({ lang }) {
  const ui = UI[lang];
  const path = lang === "es" ? "/es/contactenos" : "/contact-us";
  const pair = lang === "es" ? "/contact-us" : "/es/contactenos";
  const [form, setForm] = useState({
    name: "", phone: "", email: "", preferred_language: lang,
    case_type: "", message: "", consent: false, website: "",
  });
  const [status, setStatus] = useState("idle");
  const [errors, setErrors] = useState({});

  const page = useMemo(() => ({
    title: lang === "en" ? "Contact a Dallas Divorce Lawyer | Garcia-Windsor, P.C." : "Contacte un Abogado de Divorcio en Dallas | Garcia-Windsor, P.C.",
    description: lang === "en"
      ? "Reach Garcia-Windsor, P.C. for a free bilingual consultation in Dallas. Call 214-466-6699 or submit the online form — we respond within one business day."
      : "Contáctenos para una consulta bilingüe gratis en Dallas. Llame al 214-466-6699 o envíe el formulario — respondemos en un día hábil.",
    h1: lang === "en" ? "Contact Garcia-Windsor, P.C." : "Contáctenos — Garcia-Windsor, P.C.",
  }), [lang]);

  const schema = useMemo(() => ({
    "@context": "https://schema.org",
    "@type": "ContactPage",
    "url": `${BUSINESS.baseUrl}${path}`,
    "inLanguage": lang,
    "about": {
      "@type": "LegalService",
      "name": "Garcia-Windsor, P.C.",
      "telephone": "+1-214-466-6699",
      "address": CONTACT_ADDRESS,
    }
  }), [lang, path]);

  const breadcrumbItems = useMemo(() => ([
    { label: lang === "en" ? "Home" : "Inicio", href: lang === "en" ? "/" : "/es" },
    { label: lang === "en" ? "Contact Us" : "Contáctenos", href: path },
  ]), [lang, path]);

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = true;
    if (!form.phone.trim()) e.phone = true;
    if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) e.email = true;
    if (!form.case_type) e.case_type = true;
    if (!form.consent) e.consent = true;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      setStatus("error");
      // GA4: form validation failed. Emit the field that failed (no PII —
      // just the field name) so we can find friction points in reports.
      const failedFields = Object.keys(errors).length > 0
        ? Object.keys(errors)
        : ["name", "phone", "email", "case_type", "consent"].filter((k) => {
            if (k === "consent") return !form.consent;
            if (k === "email") return !form.email.trim() || !/\S+@\S+\.\S+/.test(form.email);
            if (k === "case_type") return !form.case_type;
            return !form[k].trim();
          });
      trackAction("form_error", {
        location: "contact_page",
        reason: "validation",
        fields: failedFields.join(","),
        error_count: failedFields.length,
      });
      // [Phase 24] A11y instrumentation — at least one role="alert"
      // message just became visible in the DOM, so a screen reader
      // will announce it. Session-scoped so we count unique visitors
      // who hit form-error announcements, not raw firing.
      trackA11yEvent("a11y_form_error_announced", {
        location: "contact_page",
        fields: failedFields.join(","),
        error_count: failedFields.length,
      });
      return;
    }
    setStatus("loading");
    trackAction("form_start", { location: "contact_page" });
    try {
      await submitContact({ ...form, source_page: path });
      setStatus("success");
      trackAction("form_submit", { location: "contact_page", case_type: form.case_type });
      // PRIMARY Google Ads conversion — only fires on Mongo write success.
      // Never bound to the button click event (that fires on validation
      // failures too and would inflate conversions with garbage).
      fireAdsConversion("formSubmission", {
        location: "contact_page",
        case_type: form.case_type,
      });
    } catch (err) {
      setStatus("error");
      trackAction("form_error", {
        location: "contact_page",
        reason: "submit_failed",
      });
      if (typeof console !== "undefined" && console.warn) {
        console.warn("[ContactPage] submit failed:", err);
      }
    }
  };

  return (
    <>
      <Seo page={page} path={path} lang={lang} pair={pair} schema={schema} />
      <section className="gw-container gw-section">
        <Breadcrumbs items={breadcrumbItems} />
        <h1>{page.h1}</h1>
        <div className="gw-contact-layout">
          <div className="gw-contact-form-col">
            {status === "success" ? (
              <div data-testid="contact-success-banner" className="gw-card" style={{background: "#E8F5EC", borderColor: "#B8DFC3", padding: "var(--gw-space-8)", textAlign: "center"}}>
                <h2 style={{color: "var(--gw-success)"}}>{lang === "en" ? "Thank you!" : "¡Gracias!"}</h2>
                <p>{lang === "en"
                  ? "We've received your message and will respond within one business day. For urgent matters, call 214-466-6699."
                  : "Hemos recibido su mensaje y le responderemos en un día hábil. Para asuntos urgentes, llame al 214-466-6699."}</p>
                <a href={`tel:${BUSINESS.phoneTel}`} className="gw-btn gw-btn-primary" style={{marginTop: "var(--gw-space-4)", display: "inline-flex", alignItems: "center", gap: 8}}>
                  <Phone size={18} strokeWidth={2.2} aria-hidden="true" /> {BUSINESS.phoneDisplay}
                </a>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="gw-contact-form" aria-describedby="contact-form-instructions">
                <p id="contact-form-instructions" className="gw-sr-only">
                  {lang === "en"
                    ? "Fields marked with an asterisk are required. All information is confidential."
                    : "Los campos marcados con asterisco son obligatorios. Toda la información es confidencial."}
                </p>
                {/* Honeypot */}
                <input type="text" name="website" tabIndex="-1" autoComplete="off"
                  value={form.website} onChange={(e) => setForm({...form, website: e.target.value})}
                  style={{position: "absolute", left: "-9999px"}} aria-hidden="true" />

                <div className="gw-form-row">
                  <label className="gw-label" htmlFor="contact-name">{lang === "en" ? "Full Name" : "Nombre Completo"} <span className="gw-required" aria-hidden="true">*</span>
                    <input id="contact-name" name="name" data-testid="contact-name-input"
                      className={`gw-input ${errors.name ? "error" : ""}`}
                      required
                      value={form.name}
                      aria-required="true"
                      aria-invalid={errors.name ? "true" : "false"}
                      aria-describedby={errors.name ? "contact-name-error" : undefined}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      autoComplete="name" />
                  </label>
                  {errors.name && (
                    <p id="contact-name-error" className="gw-field-error" role="alert" data-testid="contact-name-error">
                      {lang === "en" ? "Please enter your full name." : "Por favor ingrese su nombre completo."}
                    </p>
                  )}
                </div>
                <div className="gw-form-row two-cols">
                  <label className="gw-label" htmlFor="contact-phone">{lang === "en" ? "Phone" : "Teléfono"} <span className="gw-required" aria-hidden="true">*</span>
                    <input id="contact-phone" name="phone" data-testid="contact-phone-input"
                      className={`gw-input ${errors.phone ? "error" : ""}`}
                      required
                      type="tel"
                      value={form.phone}
                      aria-required="true"
                      aria-invalid={errors.phone ? "true" : "false"}
                      aria-describedby={errors.phone ? "contact-phone-error" : undefined}
                      onChange={(e) => setForm({...form, phone: e.target.value})}
                      autoComplete="tel" />
                  </label>
                  {errors.phone && (
                    <p id="contact-phone-error" className="gw-field-error" role="alert" data-testid="contact-phone-error">
                      {lang === "en" ? "Please enter a phone number where we can reach you." : "Por favor ingrese un teléfono donde podamos contactarle."}
                    </p>
                  )}
                  <label className="gw-label" htmlFor="contact-email">Email <span className="gw-required" aria-hidden="true">*</span>
                    <input id="contact-email" name="email" data-testid="contact-email-input"
                      className={`gw-input ${errors.email ? "error" : ""}`}
                      required
                      type="email"
                      value={form.email}
                      aria-required="true"
                      aria-invalid={errors.email ? "true" : "false"}
                      aria-describedby={errors.email ? "contact-email-error" : undefined}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      autoComplete="email" />
                  </label>
                  {errors.email && (
                    <p id="contact-email-error" className="gw-field-error" role="alert" data-testid="contact-email-error">
                      {lang === "en" ? "Please enter a valid email address." : "Por favor ingrese un correo electrónico válido."}
                    </p>
                  )}
                </div>
                <div className="gw-form-row two-cols">
                  <label className="gw-label" htmlFor="contact-preferred-language">{lang === "en" ? "Preferred Language" : "Idioma Preferido"}
                    <select id="contact-preferred-language" name="preferred_language" data-testid="contact-preferred-language-select" className="gw-select" value={form.preferred_language}
                      onChange={(e) => setForm({...form, preferred_language: e.target.value})}>
                      <option value="en">English</option>
                      <option value="es">Español</option>
                    </select>
                  </label>
                  <label className="gw-label" htmlFor="contact-case-type">{lang === "en" ? "Case Type" : "Tipo de Caso"} <span className="gw-required" aria-hidden="true">*</span>
                    <select id="contact-case-type" name="case_type" data-testid="contact-case-type-select"
                      className={`gw-select ${errors.case_type ? "error" : ""}`}
                      required
                      value={form.case_type}
                      aria-required="true"
                      aria-invalid={errors.case_type ? "true" : "false"}
                      aria-describedby={errors.case_type ? "contact-case-type-error" : undefined}
                      onChange={(e) => setForm({...form, case_type: e.target.value})}>
                      <option value="">{lang === "en" ? "Select case type…" : "Seleccione tipo de caso…"}</option>
                      {SERVICES.map((s) => (
                        <option key={s.key} value={s.key}>{lang === "en" ? s.en_title : s.es_title}</option>
                      ))}
                      <option value="business-law">{lang === "en" ? "Business Law" : "Derecho Empresarial"}</option>
                      <option value="other">{lang === "en" ? "Other" : "Otro"}</option>
                    </select>
                  </label>
                  {errors.case_type && (
                    <p id="contact-case-type-error" className="gw-field-error" role="alert" data-testid="contact-case-type-error">
                      {lang === "en" ? "Please choose a case type so we can route you to the right attorney." : "Por favor seleccione el tipo de caso para asignar el abogado correcto."}
                    </p>
                  )}
                </div>
                <div className="gw-form-row">
                  <label className="gw-label" htmlFor="contact-message">{lang === "en" ? "Brief description of your situation" : "Breve descripción de su situación"}
                    <textarea id="contact-message" name="message" data-testid="contact-message-textarea" className="gw-textarea" value={form.message}
                      onChange={(e) => setForm({...form, message: e.target.value})}
                      maxLength={5000}
                      placeholder={lang === "en" ? "Share what's happening. All information is confidential." : "Cuéntenos qué está sucediendo. Toda la información es confidencial."} />
                  </label>
                </div>
                <div className="gw-form-row">
                  <label className="gw-consent" htmlFor="contact-consent">
                    <input id="contact-consent" name="consent" data-testid="contact-consent-checkbox" type="checkbox"
                      checked={form.consent}
                      aria-required="true"
                      aria-invalid={errors.consent ? "true" : "false"}
                      aria-describedby={errors.consent ? "contact-consent-error" : undefined}
                      onChange={(e) => setForm({...form, consent: e.target.checked})} />
                    <span>
                      {lang === "en"
                        ? "I agree to be contacted about my inquiry. Submitting this form does not create an attorney-client relationship. "
                        : "Acepto ser contactado sobre mi consulta. Enviar este formulario no crea una relación abogado-cliente. "}
                      <span className="gw-required" aria-hidden="true">*</span>
                    </span>
                  </label>
                  {errors.consent && (
                    <p id="contact-consent-error" className="gw-field-error" role="alert" data-testid="contact-consent-error">
                      {lang === "en" ? "Please provide consent to proceed." : "Por favor otorgue su consentimiento."}
                    </p>
                  )}
                </div>
                <button type="submit" data-testid="contact-submit-button" className="gw-btn gw-btn-primary gw-btn-lg" style={{width: "100%"}} disabled={status === "loading"}>
                  {status === "loading" ? (lang === "en" ? "Sending…" : "Enviando…") : (lang === "en" ? "Send Message" : "Enviar Mensaje")}
                </button>
                <p className="gw-muted" style={{fontSize: "0.85rem", textAlign: "center", marginTop: "var(--gw-space-3)"}}>
                  {ui.quickResponse}
                </p>
                {status === "error" && Object.keys(errors).length > 0 && (
                  <p className="gw-form-error-summary" role="alert" data-testid="contact-form-error-summary">
                    {lang === "en" ? "Please complete all required fields." : "Por favor complete todos los campos requeridos."}
                  </p>
                )}
              </form>
            )}
          </div>

          <aside className="gw-contact-info">
            <div className="gw-card" style={{background: "var(--gw-primary-dark)", color: "#FFF9E8", borderColor: "transparent"}}>
              <span className="gw-eyebrow" style={{color: "var(--gw-accent)"}}>{lang === "en" ? "Call directly" : "Llame directamente"}</span>
              <a href={`tel:${BUSINESS.phoneTel}`} className="gw-contact-phone"
                onClick={() => trackEvent("phone_click", { location: "contact_page" })}>
                {BUSINESS.phoneDisplay}
              </a>
              <p style={{color: "rgba(255,255,255,0.9)", fontSize: "0.95rem", margin: 0}}>{ui.officeHoursShort}</p>
            </div>

            <div className="gw-card">
              <h3>{lang === "en" ? "Our Office" : "Nuestra Oficina"}</h3>
              <address style={{fontStyle: "normal", lineHeight: 1.7}}>
                <strong>Garcia-Windsor, P.C.</strong><br />
                {BUSINESS.street}<br />
                {BUSINESS.city}, {BUSINESS.state} {BUSINESS.postal}<br />
                <em>{lang === "en" ? "Bishop Arts District / Oak Cliff" : "Distrito Bishop Arts / Oak Cliff"}</em>
              </address>
              <a href={BUSINESS.mapUrl} target="_blank" rel="noopener noreferrer" className="gw-btn gw-btn-ghost" style={{marginTop: "var(--gw-space-3)"}}>
                {lang === "en" ? "Get Directions →" : "Cómo Llegar →"}
              </a>
              <div style={{marginTop: "var(--gw-space-4)", paddingTop: "var(--gw-space-4)", borderTop: "1px solid var(--gw-border)"}}>
                <strong style={{color: "var(--gw-primary-dark)"}}>{lang === "en" ? "Hours" : "Horario"}</strong>
                <p style={{whiteSpace: "pre-line", margin: 0, fontSize: "0.95rem", color: "var(--gw-text-soft)"}}>{ui.officeHoursFull}</p>
              </div>
            </div>

            <div className="gw-card" style={{background: "#FFF9E8", borderColor: "var(--gw-accent-soft)"}}>
              <h3 style={{color: "var(--gw-primary-dark)"}}>{lang === "en" ? "What Happens Next" : "Qué Sucede Después"}</h3>
              <ol style={{paddingLeft: "1.2rem", margin: 0}}>
                <li>{lang === "en" ? "We call you back within one business day." : "Le llamamos en un día hábil."}</li>
                <li>{lang === "en" ? "Free confidential consultation in English or Spanish." : "Consulta gratis confidencial en inglés o español."}</li>
                <li>{lang === "en" ? "Clear plan and fee explanation — so you know exactly what to expect." : "Plan claro y explicación de honorarios — sabrá exactamente qué esperar."}</li>
              </ol>
            </div>

            {lang === "es" && (
              <div className="gw-card" style={{background: "#FFF9E8", borderColor: "var(--gw-accent-soft)"}}>
                <h3 style={{color: "var(--gw-primary-dark)"}}>Programa PALE</h3>
                <p style={{margin: 0, fontSize: "0.95rem"}}>La abogada Isabela García es la abogada consultora exclusiva del Consulado General de México en Dallas. Si es ciudadano mexicano, pregunte por el programa PALE.</p>
              </div>
            )}
          </aside>
        </div>
      </section>

      <style>{`
        .gw-contact-layout {
          display: grid;
          grid-template-columns: 1.5fr 1fr;
          gap: var(--gw-space-8);
          margin-top: var(--gw-space-6);
        }
        @media (max-width: 900px) { .gw-contact-layout { grid-template-columns: 1fr; } }
        .gw-contact-form { background: #fff; padding: var(--gw-space-8); border-radius: var(--gw-radius-lg); box-shadow: var(--gw-shadow); border: 1px solid var(--gw-border); }
        .gw-form-row { margin-bottom: var(--gw-space-4); }
        .gw-form-row.two-cols { display: grid; grid-template-columns: 1fr 1fr; gap: var(--gw-space-3); }
        @media (max-width: 600px) { .gw-form-row.two-cols { grid-template-columns: 1fr; } }
        .gw-input.error, .gw-select.error { border-color: var(--gw-error); }
        .gw-consent { display: flex; align-items: flex-start; gap: 10px; font-weight: 400; color: var(--gw-text-soft); font-size: 0.9rem; cursor: pointer; }
        .gw-consent input[type=checkbox] { margin-top: 3px; transform: scale(1.15); accent-color: var(--gw-primary); }
        .gw-contact-info { display: flex; flex-direction: column; gap: var(--gw-space-4); }
        .gw-contact-phone {
          display: block;
          font-family: var(--gw-font-serif);
          font-size: 2rem; font-weight: 700;
          color: var(--gw-accent) !important;
          margin: var(--gw-space-2) 0;
          text-decoration: none;
        }
        .gw-contact-phone:hover { color: #FFE799 !important; text-decoration: none; }
      `}</style>
    </>
  );
}
